

import React, { useState, useRef, useEffect } from 'react';

import { FieldValidationMessage } from 'cstar-react-primitives'
import PropTypes from "prop-types";
import { FaCalendarAlt } from "react-icons/fa";
import DayPicker, { DateUtils } from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { addYears, startOfToday, format, parse, isBefore, isAfter, startOfDay, endOfDay, isToday } from 'date-fns'

import { useFormatMessage } from '../../intl/intlHooks';
import { DatePickerFieldMessage } from "./DatePickerFieldMessage";
import { datePickerMessages } from "./datePickerMessages";

const displayFormat = "MM/dd/yyyy";

export const PureDateInputSelector = ({
        value,
        disablePopupCalendar,
        inputProps,
        formatDate,
        onDayChange,
        dateFormat,
        placeholder,
        parseDate
}) => {
    const dayPickerInputRef = useRef(null);
    const onDayPickerShow = disablePopupCalendar ? () => dayPickerInputRef.current.hideDayPicker() : undefined;
    return (
        <DayPickerInput
            value={value}
            ref={dayPickerInputRef}
            inputProps={inputProps}
            onDayPickerShow={onDayPickerShow}
            formatDate={formatDate}
            onDayChange={onDayChange}
            format={dateFormat}
            placeholder={placeholder}
            parseDate={parseDate}
        />
    )
};

PureDateInputSelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date)
    ]),
    disablePopupCalendar: PropTypes.bool,
    inputProps: PropTypes.object,
    formatDate: PropTypes.func,
    onDayChange: PropTypes.func,
    dateFormat: PropTypes.string,
    placeholder: PropTypes.string,
    parseDate: PropTypes.func
};

PureDateInputSelector.defaultProps = {
    value: new Date(),
    disablePopupCalendar: false,
    inputProps: {},
    formatDate: () => {},
    onDayChange: () => {},
    dateFormat: "",
    placeholder: "YYYY-M-D",
    parseDate: () => {}
};

export const PureDayPickerSelector = ({
        selectedDays,
        todayButton,
        onDayClick,
        onTodayButtonClick,
        disabledDays,
        className,
        containerProps,
        month,
        year
}) => (
    <DayPicker
        selectedDays={selectedDays}
        todayButton={todayButton}
        onDayClick={onDayClick}
        onTodayButtonClick={onTodayButtonClick}
        disabledDays={disabledDays}
        className={className}
        containerProps={containerProps}
        month={month}
        year={year}
    />
);

PureDayPickerSelector.propTypes = {
    selectedDays: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.array
    ]),
    todayButton: PropTypes.string,
    onDayClick: PropTypes.func,
    onTodayButtonClick: PropTypes.func,
    disabledDays: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.arrayOf(PropTypes.instanceOf(Date)),
        PropTypes.object,
    ]),
    className: PropTypes.string,
    containerProps: PropTypes.object,
    month: PropTypes.instanceOf(Date),
    year: PropTypes.instanceOf(Date)
};

PureDayPickerSelector.defaultProps = {
    selectedDays: new Date(),
    todayButton: "",
    onDayClick: () => {},
    onTodayButtonClick: () => {},
    disabledDays: {},
    className: '',
    containerProps: {},
    month: new Date(),
    year: new Date()
};

const formatDate = (date) => format(date, displayFormat, {});
const parseDate = (str) => {
    if (DateUtils.isDate(str) ) {
      return str;
    }

    const parsed = parse(str, displayFormat, new Date());
    if (DateUtils.isDate(parsed) ) {
      return parsed;
    }
    return undefined;
};

export const DeliveryDatePickerUnwrapped = ({label, selected, error, setDeliveryDateFieldValue, minDate, maxDate }) => {
    const formatMessage = useFormatMessage();
    const [selectedDay, setSelectedDay] = useState(selected);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const parsedDate = parseDate(selectedDay);
    const deliveryDatePickerRoot = useRef(null);

    const handleDayChange = (selectedDayInput, modifiers, dayPickerInput) => {
        setSelectedDay(dayPickerInput.getInput().value);
    };

    const handleCalendarClick = () => {
        setDatePickerOpen(!datePickerOpen);

        if(!datePickerOpen) {
            deliveryDatePickerRoot.current.scrollIntoView({behavior: "smooth"})
        }
    };

    const dayPickerProps = {
        selectedDays: parsedDate !== undefined ? parsedDate : null,
        todayButton: formatMessage(datePickerMessages.datePickerTodayButton),
        onDayClick: (date) => {
            if(isBefore(date, maxDate) && isAfter(date, minDate))
                setSelectedDay(startOfDay(date));
                setDatePickerOpen(false);
        },
        onTodayButtonClick: () => {
            setSelectedDay(startOfToday());
            setDatePickerOpen(false);
        },
        disabledDays: {
            after: maxDate,
            before: minDate
        },
        className: "field-group__input",
        containerProps: { 'aria-live': 'polite' },
        month: parsedDate,
        year: parsedDate
    };

    const dayPickerInputProps = {
        value: isToday(parseDate(selectedDay)) ? formatMessage(datePickerMessages.datePickerToday) : selectedDay,
        disablePopupCalendar: true,
        inputProps: {
          id: "deliveryDatePicker",
          maxLength: "10",
          className: "field-group__input",
          autoComplete: "off"
        },
        formatDate,
        onDayChange: handleDayChange,
        dateFormat: displayFormat,
        placeholder: displayFormat,
        parseDate
    };

    useEffect(() => {
        setDeliveryDateFieldValue(parseDate(selectedDay))
    }, [selectedDay]);

    useEffect(() => {
        if(datePickerOpen && parseDate(selectedDay) === undefined) {
            setSelectedDay(startOfToday());
        }
    }, [datePickerOpen]);

    return (
        <div className="field-group" ref={deliveryDatePickerRoot}>
            <label htmlFor="deliveryDatePicker" className="field-group__label">{label}</label>
            <div className="personalize-datepicker field-group__div">
                {datePickerOpen ?
                    (
                        <div className="DayPickerInput-Disabled DayPickerInput field-group__input">
                            {parsedDate === undefined ? formatDate(startOfToday()) : formatDate(parsedDate)}
                        </div>
                    ) :
                        <PureDateInputSelector {...dayPickerInputProps} />
                }
                <div className="personalize-datepicker">
                    <button
                        className="DayPicker-CalendarButton"
                        aria-label={datePickerOpen ? "Close Calendar" : "Open Calendar"}
                        type="button"
                        onClick={handleCalendarClick}
                    >
                        <FaCalendarAlt className="DayPicker-CalendarButton-Icon" />
                    </button>
                </div>
            </div>
            {error &&
                (
                    <FieldValidationMessage
                        id='delivery-date-input-validation'
                        fieldName='deliveryDate'
                        validationProps={{
                            type: 'error',
                            isInvalid: true,
                            showMessage: true,
                            showIcon: true,
                            message: error
                        }}
                    />
                )
            }
            <div>
                {datePickerOpen && (<PureDayPickerSelector {...dayPickerProps} />)}
            </div>
            <DatePickerFieldMessage selectedDate={selected} />
        </div>

    )
};

export const DeliveryDatePicker = DeliveryDatePickerUnwrapped;

DeliveryDatePickerUnwrapped.propTypes = {
    error: PropTypes.string,
    label: PropTypes.string.isRequired,
    setDeliveryDateFieldValue: PropTypes.func.isRequired,
    selected: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date)
};

DeliveryDatePickerUnwrapped.defaultProps = {
    selected: '',
    error: "",
    minDate: startOfToday(),
    maxDate: endOfDay(addYears(new Date(), 1))
};
